import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { AssociationsAndSuppliers } from '@/components/AssociationsAndSuppliers';
import { Helmet } from 'react-helmet-async'

// Simple service component that matches the screenshot
function ServiceItem({ title, image, linkUrl }: { title: string; image: string; linkUrl: string }) {
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    window.history.pushState({}, '', linkUrl)
    window.dispatchEvent(new Event('popstate'))
    window.scrollTo(0, 0)
  }

  return (
    <div className="text-center">
      <a href={linkUrl} className="block" onClick={handleNavigation}>
        <div className="mb-3">
          <img 
            src={image}
            alt={`${title} services by Busy Beaver Construction - Calgary's premier deck and fence builder`}
            width={240}
            height={180}
            className="w-full h-[280px] object-cover object-center  shadow-md hover:shadow-lg transition-all"
          />
        </div>
        <h3 className="text-3xl text-gray-800">{title}</h3>
      </a>
    </div>
  )
}

export default function Services() {

  // Added Schema Data for the Services Hub Page
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://busybeaverconstruction.com/services",
        "url": "https://busybeaverconstruction.com/services",
        "name": "Services | Busy Beaver Construction | Custom Decks & Fences Calgary",
        "description": "Explore our services including custom decks, fences, gazebos, pergolas, privacy walls, railings, and outdoor living spaces in Calgary. Quality craftsmanship since 1999.",
        "isPartOf": {
          "@type": "WebSite",
          "@id": "https://busybeaverconstruction.com/#website",
          "url": "https://busybeaverconstruction.com/",
          "name": "Busy Beaver Construction"
        }
      },
      {
        "@type": "ItemList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "url": "https://busybeaverconstruction.com/services/decks",
            "name": "Decks"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "url": "https://busybeaverconstruction.com/services/gazebo",
            "name": "Gazebos"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "url": "https://busybeaverconstruction.com/services/pergolas-and-privacy-walls",
            "name": "Pergolas & Privacy Walls"
          },
          {
            "@type": "ListItem",
            "position": 4,
            "url": "https://busybeaverconstruction.com/services/living-spaces",
            "name": "Living Spaces"
          },
          {
            "@type": "ListItem",
            "position": 5,
            "url": "https://busybeaverconstruction.com/services/railings",
            "name": "Railings"
          },
          {
            "@type": "ListItem",
            "position": 6,
            "url": "https://busybeaverconstruction.com/services/fences",
            "name": "Fences"
          },
          {
            "@type": "ListItem",
            "position": 7,
            "url": "https://busybeaverconstruction.com/services/landscaping",
            "name": "Landscaping"
          }
        ]
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Services",
            "item": "https://busybeaverconstruction.com/services"
          }
        ]
      }
    ]
  };

  return (
    <main>
      <Helmet>
        <title>Services | Busy Beaver Construction | Custom Decks & Fences Calgary</title>
        <meta name="description" content="Explore our services including custom decks, fences, gazebos, pergolas, privacy walls, railings, and outdoor living spaces in Calgary. Quality craftsmanship since 1999." />
        <meta name="keywords" content="deck builder Calgary, fence contractor, gazebo builders Calgary, pergola installation, privacy walls, composite decks, outdoor living spaces, TimberTech deck builder" />
        <link rel="canonical" href="https://busybeaverconstruction.com/services" />
        <meta property="og:title" content="Services | Busy Beaver Construction | Calgary Deck Builder" />
        <meta property="og:description" content="Specializing in custom decks, fences, gazebos, pergolas, privacy walls, and complete outdoor living spaces for Calgary homeowners. Quality construction since 1999." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://busybeaverconstruction.com/services" />
        <meta property="og:image" content="https://busybeaverconstruction.com/assets/images/Decks/Composite Decks/Timbertech-Driftwood.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preload" href="/assets/images/Decks/Composite Decks/Timbertech-Driftwood.jpg" as="image" />
      </Helmet>

      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />

      <Header />

      {/* Hero Banner */}
      <section className="relative h-[400px] flex items-center justify-center">
        <img 
          src="/assets/images/Decks/Composite Decks/Timbertech-Driftwood.jpg"
          alt="TimberTech composite deck installation by Busy Beaver Construction in Calgary"
          className="absolute inset-0 w-full h-full object-cover loading-eager"
          fetchPriority="high"
        />        
        <div className="relative z-10 mt-64 text-center">
          <div className="inline-block bg-busy-green rounded py-3 mx-4 px-4 lg:px-8">
            <p className="text-white text-base font-medium rounded">Designs To Fit Your Lifestyle</p>
          </div>
        </div>
      </section>

      {/* Services Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container">
          <h1 className="text-center text-5xl lg:text-6xl font-primary mb-8 md:mb-14">Services</h1>
          
          <div className="mx-auto space-y-6 text-gray-500 leading-relaxed mb-12">
            <p className="text-center">
              At Busy Beaver Construction, we offer a wide range of services to create the ultimate backyard space.
            </p>
            
            <p>
              We specialize in custom decks to fit the needs of our clients, whether it be new, rebuilding an existing, a walkout deck, ground level deck or a front porch. In addition to quality custom decks and fences; we specialize in outdoor kitchens, gazebos, pergolas and arbours, privacy walls, railings, and deck lighting.
            </p>
            
            <p>
              From years of experience in the industry, we have close contacts with landscapers, concrete and masonry installers and many other qualified exterior contractors. This allows us to provide our clients the full-service to complete their dream backyard.
            </p>
          </div>
          
          <div className="text-center mb-16 mx-auto">
            <p className="text-xl  font-bold text-gray-500">
              With your imagination and the help of our team at Busy Beaver Construction, your outdoor living space can become a reality.
            </p>
          </div>
        </div>
          
        {/* Services Grid - Full Width */}
        <div className="max-w-full px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
            <ServiceItem 
              title="Decks"
              image="/assets/images/Decks/Composite Decks/Timbertech-Terrain-deck-in-Brown-Oak-trim-and-Sandy-Birch-surface.jpg"
              linkUrl="/services/decks"
            />
            <ServiceItem 
              title="Gazebos"
              image="/assets/images/Pergola/Cedar-Gazebo-with-shingled-roof.jpg"
              linkUrl="/services/gazebo"
            />
            <ServiceItem 
              title="Pergolas & Privacy Walls"
              image="/assets/images/Privacy Walls/Cedar-2x6-Horiztonal-slat-Privacy-wall.jpg"
              linkUrl="/services/pergolas-and-privacy-walls"
            />
            <ServiceItem 
              title="Living Spaces"
              image="/assets/images/Decks/Composite Decks/Azek-Vintage-Walnut.jpg"
              linkUrl="/services/living-spaces"
            />
            <ServiceItem 
              title="Railings"
              image="/assets/images/Railing/Aluminum-picket-railing-in-Black.jpg"
              linkUrl="/services/railings"
            />
            <ServiceItem 
              title="Fences"
              image="/assets/images/Fence/Cedar-Custom-fence-1x6-pickets-with-1x2-Horizontal-Custom-Lattice-top.jpg"
              linkUrl="/services/fences"
            />
            <ServiceItem 
              title="Landscaping"
              image="/assets/images/Landscaping/IMG_20250411_134924 (3).jpg"
              linkUrl="/services/landscaping"
            />
          </div>
        </div>
      </section>
      
      {/* Associations and Suppliers Section - now using the component */}
      <AssociationsAndSuppliers />
      <Footer />
    </main>
  )
}